import { Component, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { BaseComponent } from '@shared/classes';
import { ButtonComponent } from '@shared/components/button/button.component';
import { InputComponent } from '@shared/components/form-fields';
import { Product } from '@shared/factory';
import { Dict, ProductCheckManualTab } from '@shared/models';

import { ProductCheckService } from '../product-check.service';

@Component({
  selector: 'b2c-manual-barcode-check',
  imports: [ButtonComponent, FormsModule, InputComponent],
  styleUrl: './manual-barcode-check.component.scss',
  templateUrl: './manual-barcode-check.component.html',
})
export class ManualBarcodeCheckComponent extends BaseComponent {
  private readonly productCheckService = inject(ProductCheckService);

  tab = input.required<ProductCheckManualTab>();
  buttonStyles = input<Dict>({});

  public barcode = signal<string>('');
  public isChecking = signal(false);
  public hasSubmitted = signal(false);
  public checkFailed = signal(false);
  public checkedBarcode = signal<string>('');
  public product = signal<Product | null>(null);

  public readonly canSubmit = computed<boolean>(
    () => this.barcode().length > 0 && !this.isChecking()
  );

  onBarcodeChange(barcode: string): void {
    // `numericOnly` strips non-digits from what the field *displays*, but it
    // writes the clean value back with `emitEvent: false`, so the raw value is
    // what reaches us (a paste of `abc123` arrives verbatim). Sanitise here,
    // where we own the value that gets submitted.
    this.barcode.set(barcode.replace(/\D/g, ''));
    // Editing the barcode dismisses the previous verdict, which no longer
    // describes what is in the field — but never while a check is in flight:
    // tearing down `hasSubmitted` mid-request would blank the pending UI and
    // silently discard the response.
    if (!this.isChecking() && this.barcode() !== this.checkedBarcode()) {
      this.hasSubmitted.set(false);
      this.product.set(null);
    }
  }

  onSubmit(): void {
    if (!this.canSubmit()) {
      return;
    }
    const barcode: string = this.barcode();
    this.hasSubmitted.set(true);
    this.isChecking.set(true);
    this.checkFailed.set(false);
    this.checkedBarcode.set(barcode);
    this.product.set(null);
    // A failed product-list fetch would otherwise stick for the modal's
    // lifetime, making every re-submit re-throw the same stale error.
    this.productCheckService.retryProductsIfFailed();
    this.productCheckService
      // The check mode comes from this tab's config, not from the shared
      // service state: the scan tab may be absent (filtered out on desktop)
      // or configured with a different mode.
      .isEligible(barcode, this.tab().checkMode)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isChecking.set(false))
      )
      .subscribe({
        next: (match: Product | null) => {
          if (this.discardIfStale(barcode)) {
            return;
          }
          this.product.set(match);
        },
        error: (error: unknown) => {
          console.error('Product eligibility check failed', error);
          if (this.discardIfStale(barcode)) {
            return;
          }
          // Failure ≠ "not eligible": the shopper gets a retry message
          // instead of a false verdict.
          this.checkFailed.set(true);
          this.product.set(null);
        },
      });
  }

  /**
   * Drops a verdict that no longer describes the field. `onBarcodeChange`
   * deliberately keeps the pending UI up while a check is in flight, so a
   * shopper who keeps typing would otherwise be left with a verdict about a
   * barcode they have already edited away.
   */
  private discardIfStale(checkedBarcode: string): boolean {
    if (this.barcode() === checkedBarcode) {
      return false;
    }
    this.hasSubmitted.set(false);
    this.checkFailed.set(false);
    this.product.set(null);
    return true;
  }
}
